import { memo } from "react"
import { FiEye, FiTrash2 } from "react-icons/fi"

import type { ClassTemplateResponse } from "@/types/class_template"
import {
  Button,
  Flex,
  Heading,
  IconButton,
  Separator,
  Stack,
  Text,
} from "@chakra-ui/react"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

interface ClassTemplateCardProps {
  classTemplate: ClassTemplateResponse
  onEdit?: (classTemplate: ClassTemplateResponse) => void
  onDelete?: (classTemplate: ClassTemplateResponse) => void
  onView?: (classTemplate: ClassTemplateResponse) => void
}

const ClassTemplateCard = memo(
  ({ classTemplate, onEdit, onDelete, onView }: ClassTemplateCardProps) => {
    const titleValue = classTemplate.name ?? classTemplate.id

    const subtitleValue = classTemplate.slug ?? undefined

    // Format price as total session price (assuming 10-class session)
    const DEFAULT_SESSION_CLASSES = 10
    const formattedPrice =
      classTemplate.price != null
        ? `$${(Number(classTemplate.price) * DEFAULT_SESSION_CLASSES).toFixed(2)}`
        : "Price TBD"

    return (
      <Stack
        borderWidth="1px"
        borderRadius="lg"
        padding={4}
        gap={4}
        bg="bg.surface"
        shadow="sm"
      >
        <Flex justify="space-between" align="flex-start">
          <Stack gap={1} flex="1">
            <Heading size="sm">{titleValue}</Heading>
            {subtitleValue !== undefined && (
              <Text color="fg.muted" fontSize="sm">
                {subtitleValue || "No description"}
              </Text>
            )}
          </Stack>
          <Text
            fontSize="xl"
            fontWeight="bold"
            color="blue.600"
            textAlign="right"
          >
            {formattedPrice}
          </Text>
        </Flex>

        <Stack gap={1} fontSize="xs" color="fg.subtle">
          <Text>
            Created: {new Date(classTemplate.created_at).toLocaleString()}
          </Text>
          <Text>
            Updated: {new Date(classTemplate.updated_at).toLocaleString()}
          </Text>
        </Stack>

        <Separator />
        <Stack gap={2} fontSize="sm">
          <Flex key="name" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Name</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.name, "str")}
            </Text>
          </Flex>
          <Flex key="slug" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Slug</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.slug, "str")}
            </Text>
          </Flex>
          <Flex
            key="description"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Description</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.description, "str")}
            </Text>
          </Flex>
          <Flex
            key="program_type"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Program Type</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.program_type, "str")}
            </Text>
          </Flex>
          <Flex
            key="instructor_id"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Instructor Id</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.instructor_id, "str")}
            </Text>
          </Flex>
          <Flex
            key="swim_level"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Swim Level</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.swim_level, "str")}
            </Text>
          </Flex>
          <Flex
            key="location"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Location</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.location, "str")}
            </Text>
          </Flex>
          <Flex
            key="day_of_week"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Day Of Week</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.day_of_week, "str")}
            </Text>
          </Flex>
          <Flex
            key="start_time"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Start Time</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.start_time, "str")}
            </Text>
          </Flex>
          <Flex
            key="end_time"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">End Time</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.end_time, "str")}
            </Text>
          </Flex>
          <Flex
            key="duration_minutes"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Duration Minutes</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.duration_minutes, "int")}
            </Text>
          </Flex>
          <Flex
            key="max_students"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Max Students</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.max_students, "int")}
            </Text>
          </Flex>
          <Flex
            key="min_students"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Min Students</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.min_students, "int")}
            </Text>
          </Flex>
          <Flex
            key="current_enrollment"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Current Enrollment</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.current_enrollment, "int")}
            </Text>
          </Flex>
          <Flex
            key="waitlist_enabled"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Waitlist Enabled</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.waitlist_enabled, "bool")}
            </Text>
          </Flex>
          <Flex key="price" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Price per Class</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              $
              {classTemplate.price != null
                ? Number(classTemplate.price).toFixed(2)
                : "N/A"}
            </Text>
          </Flex>
          <Flex
            key="total_price"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Total (10-class session)</Text>
            <Text textAlign="right" flex="1" color="fg.muted" fontWeight="bold">
              {classTemplate.price != null
                ? `$${(Number(classTemplate.price) * 10).toFixed(2)}`
                : "N/A"}
            </Text>
          </Flex>
          <Flex
            key="prerequisites"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Prerequisites</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.prerequisites, "str")}
            </Text>
          </Flex>
          <Flex key="notes" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Notes</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.notes, "str")}
            </Text>
          </Flex>
          <Flex
            key="is_active"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Is Active</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.is_active, "bool")}
            </Text>
          </Flex>
          <Flex
            key="is_published"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Is Published</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classTemplate.is_published, "bool")}
            </Text>
          </Flex>
        </Stack>

        <Flex gap={2} justify="flex-end">
          {onView && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onView(classTemplate)}
            >
              <FiEye /> View
            </Button>
          )}
          {onEdit && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="blue"
              onClick={() => onEdit(classTemplate)}
            >
              Edit
            </Button>
          )}
          {onDelete && (
            <IconButton
              size="sm"
              variant="ghost"
              colorScheme="red"
              aria-label="Delete classTemplate"
              onClick={() => onDelete(classTemplate)}
            >
              <FiTrash2 />
            </IconButton>
          )}
        </Flex>
      </Stack>
    )
  },
)

ClassTemplateCard.displayName = "ClassTemplateCard"

export default ClassTemplateCard
